import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Loader2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import ConfirmSchoolNameDialog from './ConfirmSchoolNameDialog';

/**
 * Multi-step permanent delete: request OTP → verify → final confirm with school name.
 */
export default function PermanentDeleteSchoolDialog({ open, onOpenChange, school, onDeleted }) {
  const { toast } = useToast();
  const [step, setStep] = useState('otp'); // otp | final
  const [otp, setOtp] = useState('');
  const [emailMasked, setEmailMasked] = useState('');
  const [verificationToken, setVerificationToken] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [finalOpen, setFinalOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      setStep('otp');
      setOtp('');
      setEmailMasked('');
      setVerificationToken('');
      setResendIn(0);
      setFinalOpen(false);
      return;
    }
    requestOtp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, school?.id]);

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const t = setTimeout(() => setResendIn((v) => v - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const requestOtp = async () => {
    if (!school?.id) return;
    setSending(true);
    try {
      const res = await api.post(`schools/${school.id}/permanent-delete/request-otp`);
      const data = res.data?.data || {};
      setEmailMasked(data.email_masked || '');
      setResendIn(data.resend_available_in ?? 60);
      setOtp('');
      setStep('otp');
      toast({
        title: 'OTP sent',
        description: `A 6-digit code was sent to ${data.email_masked || 'your email'}.`,
      });
    } catch (err) {
      toast({
        title: 'Could not send OTP',
        description: err.response?.data?.message || 'Failed to send OTP',
        variant: 'destructive',
      });
      onOpenChange?.(false);
    } finally {
      setSending(false);
    }
  };

  const verifyOtp = async () => {
    if (!school?.id || !/^\d{6}$/.test(otp.trim())) {
      toast({ title: 'Invalid OTP', description: 'Enter the 6-digit code from your email.', variant: 'destructive' });
      return;
    }
    setVerifying(true);
    try {
      const res = await api.post(`schools/${school.id}/permanent-delete/verify-otp`, { otp: otp.trim() });
      const token = res.data?.data?.verification_token;
      if (!token) throw new Error('Missing verification token');
      setVerificationToken(token);
      setStep('final');
      onOpenChange?.(false);
      setFinalOpen(true);
    } catch (err) {
      toast({
        title: 'OTP verification failed',
        description: err.response?.data?.message || 'Invalid OTP',
        variant: 'destructive',
      });
    } finally {
      setVerifying(false);
    }
  };

  const performDelete = async () => {
    if (!school?.id || !verificationToken) return;
    setDeleting(true);
    try {
      await api.post(`schools/${school.id}/permanent-delete`, {
        verification_token: verificationToken,
        confirm_name: school.name,
      });
      toast({ title: 'School deleted', description: `${school.name} was permanently deleted.` });
      setFinalOpen(false);
      onDeleted?.(school);
    } catch (err) {
      toast({
        title: 'Deletion failed',
        description: err.response?.data?.message || 'Permanent deletion failed',
        variant: 'destructive',
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <AlertDialog open={open && step === 'otp'} onOpenChange={onOpenChange}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Verify permanent deletion</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-muted-foreground">
                <p>
                  A one-time code was sent to your registered email
                  {emailMasked ? ` (${emailMasked})` : ''}. Enter it to continue deleting{' '}
                  <span className="font-semibold text-foreground">{school?.name}</span>.
                </p>
                <div className="space-y-1.5">
                  <Label htmlFor="school-delete-otp">6-digit OTP</Label>
                  <Input
                    id="school-delete-otp"
                    inputMode="numeric"
                    maxLength={6}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    disabled={sending || verifying}
                    className="tracking-[0.35em] text-center text-lg font-semibold"
                    placeholder="••••••"
                  />
                </div>
                <p className="text-xs">OTP expires in 5 minutes. Failed attempts are limited and logged.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={sending || verifying || resendIn > 0}
              onClick={requestOtp}
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {resendIn > 0 ? `Resend in ${resendIn}s` : 'Resend OTP'}
            </Button>
            <AlertDialogCancel disabled={verifying || sending}>Cancel</AlertDialogCancel>
            <Button
              type="button"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={verifying || sending || otp.length !== 6}
              onClick={verifyOtp}
            >
              {verifying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Verify OTP
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ConfirmSchoolNameDialog
        open={finalOpen}
        onOpenChange={setFinalOpen}
        schoolName={school?.name}
        title="This action is irreversible"
        description={
          <p>
            All data belonging to this school will be permanently deleted from the application,
            database, and storage. This cannot be undone.
          </p>
        }
        confirmLabel={deleting ? 'Deleting…' : 'Permanently delete'}
        loading={deleting}
        onConfirm={performDelete}
      />
    </>
  );
}
